/**
 * i18n entry point — framework-agnostic (i18next), shared by all 3 apps + edge functions.
 *
 * Architecture (docs/plan/04-i18n-and-design.md):
 *  - App-screen namespaces: planta.* / backoffice.* / platformConsole.*
 *  - Cross-cutting domain.* vocabulary (118 terms), reused by every app and validated
 *    identically client + server.
 *  - Infra namespaces: common / auth / errors / validation / sync / notifications.
 *  - Sector OVERRIDE layers (sectors.forn / sectors.pastisseria / sectors.xarcuteria):
 *    when a tenant has a sector, the resolver looks up sectors.<sector>.<key> BEFORE
 *    falling back to the base domain term. This is wired by {@link sectorVocabulary}.
 *
 * Locale files are keyed in English code identifiers; only values are localized. The two
 * catalogs MUST have identical key sets (parity), asserted by {@link assertCatalogParity}.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { ca, type CaResources } from './ca.js';
import { es, type EsResources } from './es.js';
import { en, type EnResources } from './en.js';
import type { AppLanguage, TenantSector } from '../enums/index.js';

export { ca } from './ca.js';
export { es } from './es.js';
export { en } from './en.js';
export type { CaResources } from './ca.js';
export type { EsResources } from './es.js';
export type { EnResources } from './en.js';

/** A resource catalog: namespace → (key → string). */
export type ResourceCatalog = Record<string, Record<string, string>>;

/**
 * Bundled resources, keyed by language. ca is the reference for parity.
 *
 * The annotation is explicit on purpose: the three catalogs have grown past what `tsc` can serialize
 * for the declaration file (TS7056) when the type is inferred from `{ ca, es, en }`; naming the
 * per-language types keeps the emitted type short and identical to the inferred one.
 */
export const resources: {
  readonly ca: CaResources;
  readonly es: EsResources;
  readonly en: EnResources;
} = { ca, es, en } satisfies Record<AppLanguage, ResourceCatalog>;

/** All namespace ids present in the catalogs. */
export const namespaces = Object.keys(ca) as readonly (keyof typeof ca)[];

/** Default namespace used when none is specified by a `t()` call. */
export const defaultNS = 'common' as const;

/** Sectors that carry a vocabulary OVERRIDE layer (sectors.<sector>). */
const SECTOR_OVERRIDE_NS: Partial<Record<TenantSector, string>> = {
  forn: 'sectors.forn',
  pastisseria: 'sectors.pastisseria',
  xarcuteria: 'sectors.xarcuteria',
};

/**
 * Assert that two catalogs expose the exact same set of (namespace, key) pairs. Throws on
 * the first divergence. Run in development / tests so ca and es never drift.
 */
export function assertCatalogParity(
  a: ResourceCatalog,
  b: ResourceCatalog,
  labelA = 'ca',
  labelB = 'es',
): void {
  const keysOf = (cat: ResourceCatalog): Set<string> => {
    const out = new Set<string>();
    for (const ns of Object.keys(cat)) {
      const bundle = cat[ns];
      if (!bundle) continue;
      for (const key of Object.keys(bundle)) out.add(`${ns}:${key}`);
    }
    return out;
  };
  const ka = keysOf(a);
  const kb = keysOf(b);
  const missingInB: string[] = [];
  const missingInA: string[] = [];
  for (const k of ka) if (!kb.has(k)) missingInB.push(k);
  for (const k of kb) if (!ka.has(k)) missingInA.push(k);
  if (missingInB.length > 0 || missingInA.length > 0) {
    throw new Error(
      `i18n catalog parity broken: ` +
        `${missingInB.length} key(s) in ${labelA} missing from ${labelB} ` +
        `(${missingInB.slice(0, 5).join(', ')}…); ` +
        `${missingInA.length} key(s) in ${labelB} missing from ${labelA} ` +
        `(${missingInA.slice(0, 5).join(', ')}…)`,
    );
  }
}

/** Options for {@link createI18n}. */
export interface CreateI18nOptions {
  /** Initial language. */
  readonly lng: AppLanguage;
  /** Fallback language when a key is missing. Defaults to 'ca'. */
  readonly fallbackLng?: AppLanguage;
  /**
   * Tenant sector — enables the per-sector vocabulary override. When set, a sector key
   * shadows the base domain term (see {@link sectorVocabulary} / {@link resolveTerm}).
   */
  readonly sector?: TenantSector;
  /** Verify ca/es parity at init (recommended in dev/test). Defaults to false. */
  readonly assertParity?: boolean;
  /** Enable i18next debug logging. */
  readonly debug?: boolean;
}

/**
 * Create a framework-agnostic i18next instance preloaded with the ca/es catalogs. The
 * returned instance is NOT the global singleton (a fresh instance via createInstance), so
 * the 3 apps and edge functions can each hold their own configured instance.
 */
export function createI18n(options: CreateI18nOptions): I18nInstance {
  const { lng, fallbackLng = 'ca', assertParity = false, debug = false } = options;

  if (assertParity) {
    assertCatalogParity(resources.ca, resources.es);
    assertCatalogParity(resources.ca, resources.en, 'ca', 'en');
  }

  const instance = i18next.createInstance();
  // Initialized synchronously: resources are bundled in-memory (no backend/IO).
  void instance.init({
    lng,
    fallbackLng,
    defaultNS,
    ns: namespaces as unknown as string[],
    resources: resources as unknown as Record<
      string,
      Record<string, Record<string, string>>
    >,
    interpolation: { escapeValue: false },
    debug,
    initImmediate: false,
  });

  return instance;
}

/**
 * Resolve a domain term, honoring the sector override layer. Looks up
 * `sectors.<sector>.<key>` first (if a sector is given and the key exists there), then
 * falls back to the supplied base namespace (e.g. `domain.articles`).
 *
 * @example resolveTerm(i18n, 'domain.articles', 'finishedProduct', 'forn')
 *          → "Producte de forn" (ca) instead of the base "Producte acabat".
 */
export function resolveTerm(
  instance: I18nInstance,
  baseNamespace: string,
  key: string,
  sector?: TenantSector,
): string {
  if (sector) {
    const overrideNs = SECTOR_OVERRIDE_NS[sector];
    if (overrideNs && instance.exists(`${overrideNs}:${key}`)) {
      return instance.t(`${overrideNs}:${key}`);
    }
  }
  return instance.t(`${baseNamespace}:${key}`);
}

/**
 * Build a curried term resolver bound to a tenant's sector. Hand this to UI code so it
 * never has to know about the override mechanism — it just asks for a domain term.
 */
export function sectorVocabulary(instance: I18nInstance, sector?: TenantSector) {
  return (baseNamespace: string, key: string): string =>
    resolveTerm(instance, baseNamespace, key, sector);
}
